import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiPlus, FiEdit, FiTrash2, FiX } from 'react-icons/fi';
import { usersAPI } from '../lib/api';

// One entry per restrictable module — checked (true) means the user can see/use it. Admin-role
// accounts always have full access regardless of these flags (see backend authorizeModule /
// frontend hasModuleAccess), so this section is only meaningful for manager/user roles.
const MODULE_ACCESS_FIELDS = [
  { key: 'access_dashboard', label: 'Dashboard' },
  { key: 'access_documents', label: 'Documents' },
  { key: 'access_folders', label: 'Folders' },
  { key: 'access_ai_assistant', label: 'AI Assistant' },
  { key: 'access_search', label: 'Search' },
  { key: 'access_me_to_action', label: 'Me To Action' },
  { key: 'access_business_intelligence', label: 'Business Intelligence' },
  { key: 'access_supplier_recon', label: 'Supplier Recon' },
];

const defaultModuleAccess = () =>
  MODULE_ACCESS_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: true }), {});

const Users = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', full_name: '', role: 'user', department: '', password: '', ...defaultModuleAccess() });
  const [formError, setFormError] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.getAll(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => usersAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setShowEditForm(false);
      setEditingUser(null);
      setFormData({ email: '', full_name: '', role: 'user', department: '', password: '', ...defaultModuleAccess() });
    },
    onError: (error) => {
      // Password-strength failures (backend's validatePasswordStrength middleware) come back as
      // { error: 'Weak Password', message: '...', details: ['Password must be at least 10
      // characters long', ...] } — surface every reason at once instead of just the summary line,
      // since this previously only ever showed up in the browser's network tab, never on screen.
      const details = error.response?.data?.details;
      const detailMessages = Array.isArray(details) ? details.map(d => d.message || d).filter(Boolean) : [];
      setFormError(
        detailMessages.length > 0
          ? detailMessages.join(' ')
          : error.response?.data?.message || 'Failed to update user'
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => usersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => usersAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setShowCreateForm(false);
      setFormData({ email: '', full_name: '', role: 'user', department: '', password: '' });
      setFormError('');
    },
    onError: (error) => {
      const details = error.response?.data?.details;
      const detailMessages = Array.isArray(details) ? details.map(d => d.message || d).filter(Boolean) : [];
      setFormError(
        detailMessages.length > 0
          ? detailMessages.join(' ')
          : error.response?.data?.message || 'Failed to create user'
      );
    },
  });

  const handleSubmitForm = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.email || !formData.full_name || !formData.password) {
      setFormError('Email, name, and password are required');
      return;
    }

    if (formData.password.length < 10) {
      setFormError('Password must be at least 10 characters, with uppercase, lowercase, a number, and a special character (@$!%*?&)');
      return;
    }

    createMutation.mutate(formData);
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    const moduleAccess = MODULE_ACCESS_FIELDS.reduce(
      (acc, { key }) => ({ ...acc, [key]: user[key] !== false }),
      {}
    );
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role || 'user',
      department: user.department || '',
      password: '', // Leave empty - only update if provided
      ...moduleAccess,
    });
    setFormError('');
    setShowEditForm(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.email || !formData.full_name) {
      setFormError('Email and name are required');
      return;
    }

    // Validate password only if it's being changed
    if (formData.password && formData.password.length < 10) {
      setFormError('Password must be at least 10 characters, with uppercase, lowercase, a number, and a special character (@$!%*?&)');
      return;
    }

    const updateData = {
      email: formData.email,
      full_name: formData.full_name,
      role: formData.role,
      department: formData.department,
      ...MODULE_ACCESS_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: formData[key] }), {}),
    };

    // Only include password if it was changed
    if (formData.password) {
      updateData.password = formData.password;
    }

    updateMutation.mutate({ id: editingUser.id, data: updateData });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700',
      manager: 'bg-blue-100 text-blue-700',
      user: 'bg-green-100 text-green-700',
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600 mt-2">Manage system users and permissions</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus />
          <span>Add User</span>
        </button>
      </div>

      {/* Edit User Modal */}
      {showEditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Edit User</h3>
              <button
                onClick={() => {
                  setShowEditForm(false);
                  setEditingUser(null);
                  setFormError('');
                  setFormData({ email: '', full_name: '', role: 'user', department: '', password: '', ...defaultModuleAccess() });
                }}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Leave blank to keep current password"
                    minLength={10}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Only fill this if you want to change the password. Minimum 10 characters, with uppercase, lowercase, a number, and a special character (@$!%*?&).
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Module Access
                  </label>
                  {formData.role === 'admin' ? (
                    <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                      Admins always have full access to every module — these restrictions don't apply.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {MODULE_ACCESS_FIELDS.map(({ key, label }) => (
                        <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {formError && (
                  <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded px-3 py-2">
                    {formError}
                  </div>
                )}

                <div className="flex items-center space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="btn-primary flex-1"
                  >
                    {updateMutation.isPending ? 'Updating...' : 'Update User'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditForm(false);
                      setEditingUser(null);
                      setFormError('');
                      setFormData({ email: '', full_name: '', role: 'user', department: '', password: '', ...defaultModuleAccess() });
                    }}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Add New User</h3>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setFormError('');
                  setFormData({ email: '', full_name: '', role: 'user', department: '', password: '' });
                }}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input"
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="input"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input"
                  placeholder="••••••••"
                  minLength={10}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 10 characters, with uppercase, lowercase, a number, and a special character (@$!%*?&).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input"
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="input"
                  placeholder="e.g., Finance"
                />
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <p className="text-gray-600">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Name
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Email
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Role
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Department
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Created
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.data?.users?.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{user.full_name}</td>
                    <td className="py-3 px-4 text-gray-600">{user.email}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{user.department || 'N/A'}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this user?')) {
                              deleteMutation.mutate(user.id);
                            }
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.data?.users?.length === 0 && (
              <p className="text-gray-500 text-center py-8">No users found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
